<script setup>
import { computed, watch } from 'vue'
import { Head, useForm, router } from '@inertiajs/vue3'
import Spinner from '@pages/Shared/ui/Spinner.vue'
import Layout  from '@pages/Shared/layouts/AdminLayout.vue'
import Select  from '@pages/Shared/form/Select.vue'
import { flashToast } from '@/toast.js'

const props = defineProps({
    active_session:  Object,
    classes:         Array,
    existing_scores: Array,
    filters:         Object,
})

const loadingScores = false;

const activeTerm = computed(() =>
    props.active_session?.terms?.find(t => t.is_active) ?? null
)

const form = useForm({
    class_room_id: props.filters?.class_room_id ?? null,
    subject_id:    props.filters?.subject_id    ?? null,
    scores:        [],
})

const classOptions = computed(() =>
    props.classes.map(c => ({ label: c.name, value: c.id }))
)

const selectedClass = computed(() =>
    props.classes.find(c => c.id === form.class_room_id) ?? null
)

const subjectOptions = computed(() => {
    if (!selectedClass.value) return []
    return selectedClass.value.subjects.map(s => ({ label: s.name, value: s.id }))
})

const selectedSubject = computed(() => {
    if (!selectedClass.value || !form.subject_id) return null
    return selectedClass.value.subjects.find(s => s.id === form.subject_id) ?? null
})

const assessments = computed(() => {
    if (!selectedSubject.value) return []
    return selectedSubject.value.assessments.filter(
        a =>
            a.academic_session_id === props.active_session?.id &&
            a.academic_term_id === activeTerm.value?.id
    )
})

const students = computed(() => selectedClass.value?.students ?? [])

const canLoad = computed(() =>
    !!form.class_room_id &&
    !!form.subject_id &&
    !!props.active_session?.id &&
    !!activeTerm.value?.id
)

const scoreMap = computed(() => {
    const map = new Map()
    form.scores.forEach(s => {
        map.set(`${s.student_id}__${s.assessment_id}`, s.score)
    })
    return map
})

const getScore = (studentId, assessmentId) =>
    scoreMap.value.get(`${studentId}__${assessmentId}`) ?? ''

const studentTotal = (studentId) =>
  assessments.value.reduce((total, assessment) => {
    const score = getScore(studentId, assessment.id)
    return total + (parseFloat(score) || 0)
  }, 0)

const maxTotal = computed(() =>
    assessments.value.reduce((sum, a) => sum + Number(a.weight), 0)
)

// ─── Populate scores from server prop ────────────────────────────────────────
watch(() => props.existing_scores, (scores) => {
    if (!scores?.length || !students.value.length || !assessments.value.length) {
        form.scores = students.value.flatMap(student =>
            assessments.value.map(assessment => ({
                student_id:    student.id,
                assessment_id: assessment.id,
                score:         null,
            }))
        )
        return
    }
    form.scores = students.value.flatMap(student =>
        assessments.value.map(assessment => {
            const found = scores.find(
                e => e.student_id === student.id && e.assessment_id === assessment.id
            )
            return {
                student_id:    student.id,
                assessment_id: assessment.id,
                score:         found?.score ?? null,
            }
        })
    )
}, { immediate: true })

// ─── Selection triggers Inertia navigation (loader fires) ────────────────────
const applySelection = () => {
    const params = { filter: 1 }
    if (form.class_room_id) params.class_room_id = form.class_room_id
    if (form.subject_id)    params.subject_id    = form.subject_id

    router.get('/admin/scores', params, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

watch(() => form.class_room_id, () => {
    form.subject_id = null
    form.scores     = []
    applySelection()
})

watch(() => form.subject_id, (val) => {
    if (val) applySelection()
})

// ─── Methods ─────────────────────────────────────────────────────────────────
const updateScore = (studentId, assessmentId, value) => {

    const idx = form.scores.findIndex(
        s =>
            s.student_id === studentId &&
            s.assessment_id === assessmentId
    )

    if (idx === -1) return

    const assessment = assessments.value.find(
        a => a.id === assessmentId
    )

    const max = Number(assessment?.weight ?? 0)

    let parsed = value === '' ? null : Number(value)

    // Invalid number
    if (Number.isNaN(parsed)) {
        parsed = null
    }

    // Clamp minimum
    if (parsed !== null && parsed < 0) {
        parsed = 0
    }

    // Clamp maximum
    if (parsed !== null && parsed > max) {
        parsed = max
    }

    form.scores[idx] = {
        ...form.scores[idx],
        score: parsed,
    }
}

const handleSubmit = () => {
    if (!form.scores.length) {
        flashToast({ type: 'error', message: 'No scores to save.' })
        return
    }
    
    form.post('/admin/scores', {
        onSuccess: (page) => flashToast(page.props.flash),
    })
}
</script>

<template>
    <Layout>
        <Head title="Assessment Scores" />

        <div class="space-y-6">

            <!-- Header -->
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 class="text-xl font-bold text-primary">Assessment Scores</h2>
                    <p class="text-sm text-gray-500 mt-0.5">
                        Session: <span class="font-medium text-gray-700">{{ active_session?.name ?? '—' }}</span>
                        &nbsp;·&nbsp;
                        Term: <span class="font-medium text-gray-700">{{ activeTerm?.name ?? '—' }}</span>
                    </p>
                </div>
                <button
                    v-if="canLoad && students.length && assessments.length"
                    @click="handleSubmit"
                    :disabled="form.processing"
                    class="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-white
                           bg-primary hover:bg-primary-light rounded-lg transition
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Spinner v-if="form.processing" :size="16" />
                    Save Scores
                </button>
            </div>

            <!-- Table card -->
            <div class="bg-white border rounded-md overflow-hidden">

                <!-- Filters bar -->
                <div class="flex flex-col gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50
                            sm:flex-row sm:flex-wrap sm:items-center">
                    <Select
                        v-model="form.class_room_id"
                        :options="classOptions"
                        placeholder="Select class"
                        class="w-full sm:w-44"
                    />
                    <Select
                        v-model="form.subject_id"
                        :options="subjectOptions"
                        placeholder="Select subject"
                        :disabled="!form.class_room_id"
                        class="w-full sm:w-44"
                    />
                </div>

                <!-- Empty state -->
                <div v-if="!canLoad || !students.length || !assessments.length"
                    class="flex h-48 flex-col items-center justify-center text-center px-6">
                    <p class="text-sm text-gray-400">
                        {{ !canLoad
                            ? 'Select a class and subject to begin grading.'
                            : !assessments.length
                                ? 'No active assessments found for the current term.'
                                : 'No students found in this class.' }}
                    </p>
                </div>

                <!-- Score table -->
                <div v-if="canLoad && students.length && assessments.length" class="overflow-auto max-h-[65vh]">
                    <table class="min-w-max w-full text-sm border-collapse">

                        <thead class="sticky top-0 z-30 bg-gray-100 border-b border-gray-200">
                            <tr>
                                <th class="md:sticky left-0 z-40 bg-gray-100 px-4 py-3
                                           text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-10">
                                    #
                                </th>
                                <th class="md:sticky left-10 z-40 min-w-[200px] bg-gray-100 px-4 py-3
                                           text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Student
                                </th>
                                <th v-for="assessment in assessments" :key="assessment.id" class="px-4 py-3 text-center">
                                    <span class="text-xs font-semibold text-gray-700 whitespace-nowrap">
                                        {{ assessment.name }}
                                        <span class="text-gray-500 font-normal">/ {{ assessment.weight }}</span>
                                    </span>
                                </th>
                                <th class="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                    Total <span class="text-gray-500 font-normal normal-case">/ {{ maxTotal }}</span>
                                </th>
                            </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-200">
                            <tr v-for="(student, i) in students" :key="student.id"
                                class="hover:bg-gray-50 transition-colors">
                                <td class="md:sticky left-0 z-20 bg-white px-4 py-3 text-xs text-gray-500">
                                    {{ i + 1 }}
                                </td>
                                <td class="md:sticky left-10 z-20 bg-white px-4 py-3">
                                    <div class="flex flex-col">
                                        <span class="text-sm font-medium text-gray-800">{{ student.name }}</span>
                                        <span class="text-xs text-gray-500 uppercase tracking-wider">{{ student.id }}</span>
                                    </div>
                                </td>
                                <td v-for="assessment in assessments" :key="assessment.id"
                                    class="px-4 py-3 text-center">
                                    <input
                                        type="number"
                                        :max="assessment.weight"
                                        min="0"
                                        :value="getScore(student.id, assessment.id)"
                                        @blur="updateScore(student.id, assessment.id, $event.target.value)"
                                        class="h-9 w-20 rounded-md border border-gray-300 bg-gray-50 px-2
                                               text-center text-sm font-medium text-gray-700
                                               focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10
                                               hover:border-gray-300 transition outline-none"
                                    />
                                </td>
                                <td class="px-4 py-3 text-center">
                                    <span class="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full border"
                                        :class="studentTotal(student.id) === maxTotal
                                            ? 'bg-green-50 text-green-700 border-green-100'
                                            : 'bg-primary-50 text-primary border-primary-100'">
                                        {{ studentTotal(student.id) }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>

                    </table>
                </div>

                <!-- Sticky footer -->
                <div v-if="canLoad && students.length && assessments.length"
                    class="sticky bottom-0 flex justify-end px-4 py-3 bg-gray-50 border-t border-gray-100">
                    <button
                        @click="handleSubmit"
                        :disabled="form.processing"
                        class="flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 text-sm font-semibold text-white
                               bg-primary hover:bg-primary-light rounded-lg transition
                               disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Spinner v-if="form.processing" :size="16" />
                        Save Scores
                    </button>
                </div>

            </div>
        </div>
    </Layout>
</template>